import { useState } from "react";
import { Shield, Zap, AlertCircle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useDualCredits } from "@/hooks/useDualCredits";
import type { SpotRateResult } from "@/lib/spotRateEngine";

interface PreflightCheck {
  label: string;
  status: "ok" | "warn" | "none";
}

function getPreflightChecks(spotRate: SpotRateResult | null | undefined, rateAmount: string): PreflightCheck[] {
  if (!spotRate || !spotRate.compCount) {
    return [
      { label: "Awaiting lane data — fill in origin, destination & equipment.", status: "none" },
    ];
  }

  const userRate = parseFloat((rateAmount || "0").replace(/,/g, ""));
  const marketRate = spotRate.marketRate;
  const checks: PreflightCheck[] = [];

  // Rate competitiveness
  if (userRate > 0 && marketRate > 0) {
    const diff = ((userRate - marketRate) / marketRate) * 100;
    if (diff >= -5) {
      checks.push({ label: `Rate is competitive for this lane ($${marketRate.toLocaleString()} avg).`, status: "ok" });
    } else {
      checks.push({ label: `Rate is ${Math.abs(Math.round(diff))}% below market avg ($${marketRate.toLocaleString()}).`, status: "warn" });
    }
  } else {
    checks.push({ label: `Market rate: $${marketRate.toLocaleString()} — enter your rate.`, status: "none" });
  }

  // Carrier interest from comp count
  if (spotRate.compCount >= 5) {
    checks.push({ label: "Expected carrier interest: High.", status: "ok" });
  } else if (spotRate.compCount >= 2) {
    checks.push({ label: "Expected carrier interest: Moderate.", status: "ok" });
  } else {
    checks.push({ label: "Limited lane data — carrier interest uncertain.", status: "warn" });
  }

  // Confidence / risk
  if (spotRate.confidence === "high") {
    checks.push({ label: "Risk: Low.", status: "ok" });
  } else if (spotRate.confidence === "medium") {
    checks.push({ label: "Risk: Medium — limited comps.", status: "warn" });
  } else {
    checks.push({ label: "Risk: Higher — sparse lane data.", status: "warn" });
  }

  return checks;
}

function getRiskLabel(spotRate: SpotRateResult | null | undefined): string {
  if (!spotRate || !spotRate.compCount) return "No Data";
  if (spotRate.confidence === "high") return "Low Risk";
  if (spotRate.confidence === "medium") return "Med Risk";
  return "Higher Risk";
}

function AIPreflightChecks({ checks }: { checks: PreflightCheck[] }) {
  return (
    <div className="space-y-1">
      {checks.map((c) => (
        <div key={c.label} className="flex items-center gap-2 text-[10px] text-muted-foreground">
          <span
            className="h-1.5 w-1.5 rounded-full flex-shrink-0"
            style={{
              backgroundColor:
                c.status === "ok"
                  ? "hsl(var(--success))"
                  : c.status === "warn"
                  ? "hsl(var(--warning))"
                  : "hsl(var(--muted-foreground))",
            }}
          />
          {c.label}
        </div>
      ))}
    </div>
  );
}

export interface BoostOption {
  duration: number;
  credits: number;
}

const BOOST_TIERS: BoostOption[] = [
  { duration: 10, credits: 10 },
  { duration: 20, credits: 19 },
  { duration: 30, credits: 27 },
];

interface ContactPublishCardProps {
  rateAmount: string;
  auctionMode: boolean;
  onRateChange: (v: string) => void;
  onAuctionModeChange: (v: boolean) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  totalMiles: number | null;
  isEditing?: boolean;
  saveAsTemplate?: boolean;
  onSaveAsTemplateChange?: (v: boolean) => void;
  boostOption?: BoostOption | null;
  onBoostOptionChange?: (v: BoostOption | null) => void;
  spotRate?: SpotRateResult | null;
}

export default function ContactPublishCard({
  rateAmount,
  auctionMode,
  onRateChange,
  onAuctionModeChange,
  onSubmit,
  isSubmitting,
  totalMiles,
  isEditing,
  saveAsTemplate,
  onSaveAsTemplateChange,
  boostOption,
  onBoostOptionChange,
  spotRate,
}: ContactPublishCardProps) {
  const [boostEnabled, setBoostEnabled] = useState(false);
  const { companyBalance, userBalance, totalAvailable, loading: creditsLoading } = useDualCredits();
  const balance = totalAvailable;

  const handleBoostToggle = (enabled: boolean) => {
    setBoostEnabled(enabled);
    if (!enabled) {
      onBoostOptionChange?.(null);
    } else if (!boostOption) {
      onBoostOptionChange?.(BOOST_TIERS[0]);
    }
  };

  const handleTierChange = (val: string) => {
    const tier = BOOST_TIERS.find(t => t.duration === Number(val));
    if (tier) onBoostOptionChange?.(tier);
  };

  const insufficientCredits = boostEnabled && boostOption && balance < boostOption.credits;
  const preflightChecks = getPreflightChecks(spotRate, rateAmount);
  const riskLabel = getRiskLabel(spotRate);

  return (
    <div
      className="rounded-xl border border-border bg-card flex flex-col"
      style={{ boxShadow: "var(--shadow-card)" }}
    >
      {/* Preflight */}
      <div className="px-4 pt-4 pb-3 border-b border-border">
        <div className="flex items-center gap-1.5 mb-1.5">
          <Shield className="h-3 w-3" style={{ color: "hsl(var(--brand-teal-deep))" }} />
          <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "hsl(var(--brand-teal-deep))" }}>
            AI Preflight
          </span>
        </div>
        <AIPreflightChecks checks={preflightChecks} />
      </div>

      {/* Boost This Load */}
      {!isEditing && (
        <div className="px-3 py-2 border-b border-border space-y-2">
          <div className="flex items-center gap-1.5">
            <Zap className="h-3 w-3" style={{ color: "hsl(var(--brand-teal-deep))" }} />
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "hsl(var(--brand-teal-deep))" }}>
              Boost This Load
            </span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="h-3 w-3" style={{ color: "hsl(var(--brand-teal-deep))" }} />
              <span className="text-xs font-semibold text-foreground">Feature this load</span>
            </div>
            <Switch checked={boostEnabled} onCheckedChange={handleBoostToggle} />
          </div>
          {!boostEnabled ? (
            <p className="text-[11px] text-muted-foreground">Enable to boost visibility on this lane.</p>
          ) : (
            <>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Boosted loads appear in a Featured Loads rail above search results.
              </p>
              <div
                className="rounded-md px-3 py-1.5 text-[11px]"
                style={{ backgroundColor: "hsl(170 85% 32% / 0.08)" }}
              >
                <span style={{ color: "hsl(var(--brand-teal-deep))" }} className="font-semibold">
                  💎 Available: {creditsLoading ? "…" : `${companyBalance} company + ${userBalance} personal = ${totalAvailable} total`}
                </span>
              </div>

              <RadioGroup
                value={String(boostOption?.duration ?? 10)}
                onValueChange={handleTierChange}
                className="space-y-1"
              >
                {BOOST_TIERS.map((tier) => (
                  <label
                    key={tier.duration}
                    className="flex items-center gap-2 rounded-md px-2.5 py-1.5 border border-border cursor-pointer hover:border-brand-teal/40 transition-colors"
                  >
                    <RadioGroupItem value={String(tier.duration)} />
                    <span className="text-xs font-semibold text-foreground flex-1">
                      {tier.duration} minutes
                    </span>
                    <span className="text-xs font-bold" style={{ color: "hsl(var(--brand-teal-deep))" }}>
                      {tier.credits} credits
                    </span>
                  </label>
                ))}
              </RadioGroup>

              {insufficientCredits && (
                <div className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 bg-destructive/10 text-destructive text-[11px]">
                  <AlertCircle className="h-3 w-3 flex-shrink-0" />
                  <span>Insufficient credits. Purchase personal credits or contact your admin for company credits.</span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Rate + Auction */}
      <div className="px-4 py-3 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <span className="text-[10px] font-medium block mb-1 text-muted-foreground">
              Post Rate
            </span>
            <div className="flex items-center rounded-md overflow-hidden border border-input">
              <span className="px-2 py-1.5 text-xs font-medium bg-muted text-muted-foreground border-r border-input">
                $
              </span>
              <Input
                className="border-0 focus-visible:ring-0 text-sm font-bold w-full h-8"
                value={rateAmount}
                onChange={(e) => onRateChange(e.target.value)}
                placeholder="0.00"
              />
            </div>
            {totalMiles && rateAmount && parseFloat(rateAmount.replace(/,/g, "")) > 0 && (
              <span className="text-[10px] text-muted-foreground mt-0.5 block">
                ${(parseFloat(rateAmount.replace(/,/g, "")) / totalMiles).toFixed(2)}/mi
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 pt-4">
            <Checkbox
              id="auction-bar"
              checked={auctionMode}
              onCheckedChange={(c) => onAuctionModeChange(!!c)}
            />
            <Label htmlFor="auction-bar" className="text-[10px] cursor-pointer whitespace-nowrap text-muted-foreground">
              Auction Mode
            </Label>
          </div>
        </div>
      </div>

      {/* Save as Template */}
      {!isEditing && onSaveAsTemplateChange && (
        <div className="px-4 py-2.5 border-b border-border">
          <div className="flex items-center gap-2">
            <Checkbox
              id="save-template"
              checked={saveAsTemplate ?? false}
              onCheckedChange={(c) => onSaveAsTemplateChange(!!c)}
            />
            <Label htmlFor="save-template" className="text-xs cursor-pointer text-muted-foreground">
              Save this load as a reusable template
            </Label>
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="px-4 py-4">
        <Button
          onClick={onSubmit}
          disabled={isSubmitting || !!insufficientCredits}
          className="w-full h-12 text-base font-bold uppercase tracking-wide transition-all duration-150 hover:shadow-lg active:scale-[0.98] flex items-center justify-center gap-3"
          style={{
            background: "linear-gradient(135deg, hsl(170 100% 50%), hsl(170 85% 32%))",
            color: "hsl(215 60% 12%)",
            boxShadow: "0 4px 20px hsl(170 100% 50% / 0.3)",
          }}
        >
          {isSubmitting ? (isEditing ? "SAVING..." : "POSTING...") : (isEditing ? "SAVE CHANGES" : "POST LOAD")}
          {boostEnabled && boostOption && !isEditing && (
            <span
              className="rounded-full px-2 py-0.5 text-[10px] font-bold"
              style={{
                backgroundColor: "hsl(215 60% 12% / 0.15)",
                color: "hsl(215 60% 12%)",
              }}
            >
              ⚡ +{boostOption.credits} cr
            </span>
          )}
          {!boostEnabled && (
            <span
              className="rounded-full px-2 py-0.5 text-[10px] font-bold"
              style={{
                backgroundColor: "hsl(215 60% 12% / 0.15)",
                color: "hsl(215 60% 12%)",
              }}
            >
              {riskLabel}
            </span>
          )}
        </Button>
      </div>
    </div>
  );
}
